import React, {useState, useRef, useCallback} from 'react';
import {
  View,
  Text,
  Image,
  StyleSheet,
  TouchableOpacity,
  StatusBar,
  Linking,
  TextInput,
  Animated,
  Keyboard,
  FlatList,
  ActivityIndicator,
  Dimensions,
} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {useNavigation} from '@react-navigation/native';
import {NativeStackNavigationProp} from '@react-navigation/native-stack';
import {Icon} from './Icon';
import {useTheme} from '../hooks/useTheme';
import {useTabNavigation} from '../hooks/useTabNavigation';
import {useAuth} from '../hooks/useAuth';
import {useArticleNavigation} from '../hooks/useArticleNavigation';
import {ArticleCard} from './ArticleCard';
import {Article} from '../types/article';
import {API_CONFIG} from '../config/api';

interface HeaderProps {
  showBackButton?: boolean;
  onBackPress?: () => void;
  rightAction?: React.ReactNode;
  articleUrl?: string; // For "open in browser" action
  showActionBar?: boolean; // Show back/browser action bar
  onSearch?: (query: string) => void; // Search submit callback
  onSearchActiveChange?: (active: boolean) => void; // Called when search activates/deactivates
  onSearchQueryChange?: (query: string) => void; // Called as user types
  showSearch?: boolean; // Enable inline search
}

type RootStackParamList = {
  Home: {tab?: string} | undefined;
  Login: undefined;
  Article: any;
  Search: undefined;
  Profile: undefined;
};

export const Header: React.FC<HeaderProps> = ({
  onBackPress,
  rightAction,
  articleUrl,
  showActionBar = false,
  onSearch,
  onSearchActiveChange,
  onSearchQueryChange,
  showSearch = true,
}) => {
  const insets = useSafeAreaInsets();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const {user, isAuthenticated} = useAuth();
  const {colors, isDark} = useTheme();
  const {goHome, goToProfile} = useTabNavigation();

  // Search state
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Article[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showAllSearchResults, setShowAllSearchResults] = useState(false);
  const searchInputRef = useRef<TextInput>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Use article navigation hook for proper DIRETTA handling
  const {handleArticlePress} = useArticleNavigation(searchResults);
  
  // Animation values
  const logoOpacity = useRef(new Animated.Value(1)).current;
  const searchWidth = useRef(new Animated.Value(0)).current;
  const profileOpacity = useRef(new Animated.Value(1)).current;

  // Logo URL - use white text logo for both since both headers are now purple/dark
  const logoUrl = 'https://cdn.laviola.it/laviola/logo-payoff.png';

  const getInitials = (name: string) => {
    const words = name.trim().split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const handleProfilePress = () => {
    if (goToProfile) {
      goToProfile();
    } else {
      // Fallback for screens strictly outside the main tab navigator
      navigation.navigate('Home', {tab: 'profile'});
    }
  };

  const handleOpenInBrowser = () => {
    if (articleUrl) {
      Linking.openURL(articleUrl);
    }
  };

  const activateSearch = () => {
    setIsSearchActive(true);
    onSearchActiveChange?.(true);
    
    // Animate: fade out logo/profile, expand search
    Animated.parallel([
      Animated.timing(logoOpacity, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.timing(profileOpacity, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.timing(searchWidth, {
        toValue: 1,
        duration: 250,
        useNativeDriver: false,
      }),
    ]).start(() => {
      searchInputRef.current?.focus();
    });
  };

  const deactivateSearch = useCallback(() => {
    Keyboard.dismiss();
    setSearchQuery('');
    setSearchResults([]);
    setShowAllSearchResults(false);
    onSearchActiveChange?.(false);
    onSearchQueryChange?.('');
    
    // Animate: fade in logo/profile, shrink search
    Animated.parallel([
      Animated.timing(logoOpacity, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.timing(profileOpacity, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.timing(searchWidth, {
        toValue: 0,
        duration: 250,
        useNativeDriver: false,
      }),
    ]).start(() => {
      setIsSearchActive(false);
    });
  }, [logoOpacity, profileOpacity, searchWidth, onSearchActiveChange, onSearchQueryChange]);

  // Perform search API call
  const performSearch = useCallback(async (query: string) => {
    if (!query.trim()) return;
    
    setIsSearching(true);
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/app/search?q=${encodeURIComponent(query)}`,
      );
      const json = await response.json();
      if (json.success && json.posts) {
        setSearchResults(json.posts);
      } else {
        setSearchResults([]);
      }
    } catch (err) {
      console.error('Search error:', err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Handle search query change (live search with debounce)
  const handleSearchQueryChangeInternal = useCallback((query: string) => {
    setSearchQuery(query);
    setShowAllSearchResults(false);
    onSearchQueryChange?.(query);
    
    // Clear previous debounce timer
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    
    // Debounced search - only search if query has 2+ characters
    if (query.length >= 2) {
      searchDebounceRef.current = setTimeout(() => {
        performSearch(query);
      }, 400);
    } else {
      setSearchResults([]);
    }
  }, [performSearch, onSearchQueryChange]);

  const handleSearchSubmit = () => {
    if (searchQuery.trim()) {
      performSearch(searchQuery.trim());
      onSearch?.(searchQuery.trim());
    }
  };

  // Handle article press from search results - uses shared navigation for DIRETTA support
  const handleSearchResultPress = useCallback((article: Article, index: number) => {
    deactivateSearch();
    handleArticlePress(article, index);
  }, [deactivateSearch, handleArticlePress]);

  const dynamicStyles = {
    statusBarBackground: {
      backgroundColor: colors.headerBackground,
    },
    container: {
      backgroundColor: colors.headerBackground,
    },
    headerText: {
      color: colors.headerText,
    },
    actionBar: {
      backgroundColor: isDark ? colors.surface : colors.surfaceVariant,
      borderBottomColor: colors.border,
    },
  };

  // Interpolate search bar width
  const animatedSearchStyle = {
    flex: searchWidth.interpolate({
      inputRange: [0, 1],
      outputRange: [1, 10], // Expand to take more space
    }),
  };

  return (
    <>
      <StatusBar 
        barStyle="light-content"
        backgroundColor={colors.headerBackground} 
      />
      {/* Background extending to top of screen */}
      <View style={[styles.statusBarBackground, dynamicStyles.statusBarBackground, {height: insets.top}]} />
      
      {/* Main Header */}
      <View style={[styles.container, dynamicStyles.container]}>
        {/* Left - Logo (animated fade) */}
        {!isSearchActive && (
          <Animated.View style={[styles.leftSection, {opacity: logoOpacity}]}>
            <TouchableOpacity onPress={() => { goHome(); if (navigation.canGoBack()) { navigation.popToTop(); } }} activeOpacity={0.7}>
              <Image
                source={{uri: logoUrl}}
                style={styles.logo}
                resizeMode="contain"
              />
            </TouchableOpacity>
          </Animated.View>
        )}

        {/* Search Bar - Expandable */}
        {showSearch && (
          <View style={styles.searchWrapper}>
            <Animated.View style={[
              styles.searchBar, 
              {backgroundColor: isSearchActive ? '#FFFFFF' : 'rgba(255,255,255,0.15)'},
              isSearchActive && animatedSearchStyle,
            ]}>
              {isSearchActive ? (
                <>
                  <TextInput
                    ref={searchInputRef}
                    style={styles.searchInput}
                    placeholder="Cerca articoli..."
                    placeholderTextColor="#999"
                    value={searchQuery}
                    onChangeText={handleSearchQueryChangeInternal}
                    onSubmitEditing={handleSearchSubmit}
                    returnKeyType="search"
                    autoCapitalize="none"
                    autoCorrect={false}
                  />
                  <TouchableOpacity 
                    style={styles.searchIconButton}
                    onPress={handleSearchSubmit}
                    activeOpacity={0.8}
                  >
                    <Icon name="magnifying-glass" size={16} color="#5C258D" />
                  </TouchableOpacity>
                </>
              ) : (
                <TouchableOpacity
                  style={styles.searchTouchable}
                  onPress={activateSearch}
                  activeOpacity={0.7}
                >
                  <Icon name="magnifying-glass" size={14} color="rgba(255,255,255,0.6)" />
                  <Text style={styles.searchPlaceholder}>Cerca...</Text>
                </TouchableOpacity>
              )}
            </Animated.View>
            
            {/* X button outside search box */}
            {isSearchActive && (
              <TouchableOpacity 
                style={styles.cancelButtonOutside}
                onPress={deactivateSearch}
                activeOpacity={0.7}
              >
                <Icon name="xmark" size={18} color="#FFFFFF" />
              </TouchableOpacity>
            )}
          </View>
        )}

        {/* Right - Auth (animated fade) */}
        {!isSearchActive && (
          <Animated.View style={[styles.rightSection, {opacity: profileOpacity}]}>
            {/* Auth Button */}
            {rightAction || (
              <TouchableOpacity 
                style={styles.authButtonClean}
                onPress={handleProfilePress}
                activeOpacity={0.7}
              >
                {isAuthenticated && user ? (
                  <View style={styles.userSection}>
                    {user.avatar ? (
                      <Image source={{uri: user.avatar}} style={styles.avatar} />
                    ) : (
                      <View style={[styles.initialsCircle, {backgroundColor: colors.headerText}]}>
                        <Text style={[styles.initialsText, {color: colors.headerBackground}]}>
                          {getInitials(user.name)}
                        </Text>
                      </View>
                    )}
                    <Text style={[styles.userName, dynamicStyles.headerText]} numberOfLines={1}>
                      {user.name.split(' ')[0]}
                    </Text>
                  </View>
                ) : (
                  <View style={styles.guestIconContainer}>
                    <View style={[styles.guestCircle, {backgroundColor: 'rgba(255,255,255,0.2)'}]}>
                      <Icon name="user" size={14} color="#FFFFFF" />
                    </View>
                    <View style={styles.redBadge} />
                  </View>
                )}
              </TouchableOpacity>
            )}
          </Animated.View>
        )}
      </View>

      {/* Action Bar (for articles) */}
      {showActionBar && (
        <View style={[styles.actionBar, dynamicStyles.actionBar]}>
          <TouchableOpacity 
            style={styles.actionButton} 
            onPress={onBackPress}
            activeOpacity={0.7}
          >
            <Icon name="chevron-left" size={16} color={colors.textSecondary} />
            <Text style={[styles.actionButtonText, {color: colors.textSecondary}]}>Indietro</Text>
          </TouchableOpacity>

          {articleUrl && (
            <TouchableOpacity 
              style={styles.actionButton} 
              onPress={handleOpenInBrowser}
              activeOpacity={0.7}
            >
              <Text style={[styles.actionButtonText, {color: colors.primary}]}>Apri nel browser</Text>
              <Icon name="globe" size={14} color={colors.primary} />
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* Search Results Overlay */}
      {isSearchActive && (
        <View style={[styles.searchOverlay, {backgroundColor: colors.background, top: insets.top + 50}]}>
          {isSearching ? (
            <View style={styles.searchLoading}>
              <ActivityIndicator size="small" color={colors.primary} />
              <Text style={[styles.searchLoadingText, {color: colors.textSecondary}]}>
                Ricerca in corso...
              </Text>
            </View>
          ) : searchQuery.length < 2 ? (
            <View style={styles.searchHint}>
              <Text style={[styles.searchHintText, {color: colors.textSecondary}]}>
                Digita almeno 2 caratteri per cercare
              </Text>
            </View>
          ) : searchResults.length === 0 ? (
            <View style={styles.searchHint}>
              <Text style={[styles.searchHintText, {color: colors.textSecondary}]}>
                Nessun risultato trovato per "{searchQuery}"
              </Text>
            </View>
          ) : (
            <FlatList
              data={showAllSearchResults ? searchResults : searchResults.slice(0, 4)}
              renderItem={({item, index}) => (
                <ArticleCard
                  article={item}
                  onPress={() => handleSearchResultPress(item, index)}
                />
              )}
              keyExtractor={(item) => `search-${item.id}`}
              contentContainerStyle={styles.searchResultsList}
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
              ListFooterComponent={
                !showAllSearchResults && searchResults.length > 4 ? (
                  <TouchableOpacity 
                    style={styles.showAllButton}
                    onPress={() => setShowAllSearchResults(true)}
                    activeOpacity={0.8}
                  >
                    <Text style={[styles.showAllButtonText, {color: colors.primary}]}>
                      Mostra tutti i risultati per "{searchQuery}"
                    </Text>
                  </TouchableOpacity>
                ) : null
              }
            />
          )}
        </View>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  statusBarBackground: {},
  container: {
    height: 50,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
  },
  leftSection: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  rightSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  logo: {
    width: 110,
    height: 30,
  },
  searchWrapper: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 12,
  },
  searchBar: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    height: 36,
    borderRadius: 18,
    paddingHorizontal: 12,
  },
  searchTouchable: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  searchPlaceholder: {
    fontSize: 13,
    fontWeight: '400',
    color: 'rgba(255,255,255,0.6)',
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: '#333',
    paddingVertical: 0,
    paddingHorizontal: 4,
  },
  searchIconButton: {
    width: 28,
    height: 28,
    justifyContent: 'center',
    alignItems: 'center',
  },
  searchButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#5C258D',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 8,
  },
  cancelButton: {
    width: 28,
    height: 28,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 4,
  },
  cancelButtonOutside: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 8,
  },

  authButton: {
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 16,
  },
  authButtonClean: {
    padding: 4,
  },
  guestIconContainer: {
    position: 'relative',
  },
  redBadge: {
    position: 'absolute',
    top: -2,
    right: -2,
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#E53935',
    borderWidth: 1.5,
    borderColor: '#5C258D',
  },
  loginSection: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  loginText: {
    fontSize: 13,
    fontWeight: '500',
    marginLeft: 6,
  },
  userSection: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatar: {
    width: 24,
    height: 24,
    borderRadius: 12,
    marginRight: 6,
  },
  initialsCircle: {
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 6,
  },
  guestCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  initialsText: {
    fontSize: 10,
    fontWeight: '700',
  },
  userName: {
    fontSize: 13,
    fontWeight: '500',
    maxWidth: 70,
  },
  // Action Bar styles
  actionBar: {
    height: 44,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    borderBottomWidth: 1,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 4,
    gap: 4,
  },
  actionButtonText: {
    fontSize: 14,
    fontWeight: '500',
  },
  // Search overlay styles
  searchOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    height: Dimensions.get('window').height,
    zIndex: 100,
  },
  searchLoading: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 40,
  },
  searchLoadingText: {
    marginTop: 12,
    fontSize: 14,
  },
  searchHint: {
    flex: 1,
    paddingTop: 40,
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  searchHintText: {
    fontSize: 14,
    textAlign: 'center',
  },
  searchResultsList: {
    paddingVertical: 8,
    paddingBottom: 100,
  },
  showAllButton: {
    paddingVertical: 16,
    paddingHorizontal: 20,
    alignItems: 'center',
    marginTop: 8,
    marginBottom: 100,
  },
  showAllButtonText: {
    fontSize: 15,
    fontWeight: '600',
    textAlign: 'center',
  },
});
